import { Component, computed, EventEmitter, input, Input, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { TranslocoModule } from '@jsverse/transloco';
import { ButtonDirective, CheckboxComponent } from 'shared/ui-common';

import { TimeSpanDto, TimeSpanType } from '@treetop/shared/model';

import { IntervalInputComponent } from './interval-input/interval-input.component';

@Component({
  standalone: true,
  imports: [FormsModule, TranslocoModule, MatSelectModule, CheckboxComponent, IntervalInputComponent, ButtonDirective],
  selector: 'sidebar-recurrence',
  templateUrl: './recurrence.component.html',
  styleUrls: ['./recurrence.component.scss'],
})
export class RecurrenceComponent {
  public recurrenceInterval = input<TimeSpanDto | undefined>(undefined);
  public recurrence = input<number | undefined>(undefined);

  @Input() public isSidebarLink = false;

  @Output() public recurrenceIntervalChanged = new EventEmitter<TimeSpanDto>();
  @Output() public recurrenceChanged = new EventEmitter<number>();

  protected fromDateType = computed(() => this.extractNumberAndDateType(this.recurrenceInterval().from).dateType);
  protected toDateType = computed(() => this.extractNumberAndDateType(this.recurrenceInterval().to).dateType);
  protected fromNumber = computed(() => this.extractNumberAndDateType(this.recurrenceInterval().from).value);
  protected toNumber = computed(() => this.extractNumberAndDateType(this.recurrenceInterval().to).value);

  protected selectRecurrenceIntervalType(recurrenceIntervalType: number) {
    if (recurrenceIntervalType === this.recurrenceInterval()?.type) {
      this.recurrenceIntervalChanged.emit(null);
      return;
    }

    const newRecurrenceInterval = {
      ...{ to: '0mo', from: '0mo' },
      ...this.recurrenceInterval(),
      type: recurrenceIntervalType,
    };

    this.recurrenceIntervalChanged.emit(newRecurrenceInterval);
  }

  private extractNumberAndDateType(data: string): { value: number; dateType: string } {
    if (!data) {
      return { value: 0, dateType: 'mo' };
    }

    const pattern = /a|mo|wk|d|h|c/;
    const match = data.match(pattern);
    if (match) {
      const value = parseInt(data.split(match[0])[0]);
      const dateType = match[0];
      return { value, dateType };
    }

    return { value: 0, dateType: 'mo' };
  }

  protected handleRecurrenceChanged(recurrence: number, infinite?: boolean) {
    if (infinite) {
      this.recurrenceChanged.emit(-1);
      return;
    }

    this.recurrenceChanged.emit(recurrence);
  }

  protected handleFromDateTypeChanged($event: string) {
    const newRecurrenceInterval: TimeSpanDto = {
      ...this.recurrenceInterval(),
      from: this.calculateTimeSpan(this.fromNumber(), $event),
    };

    this.recurrenceIntervalChanged.emit(newRecurrenceInterval);
  }

  protected handleToDateTypeChanged($event: string) {
    const newRecurrenceInterval: TimeSpanDto = {
      ...this.recurrenceInterval(),
      to: this.calculateTimeSpan(this.toNumber(), $event),
    };

    this.recurrenceIntervalChanged.emit(newRecurrenceInterval);
  }

  protected handleFromNumberChanged(fromNumber: number) {
    const newRecurrenceInterval: TimeSpanDto = {
      ...this.recurrenceInterval(),
      from: this.calculateTimeSpan(fromNumber ?? 0, this.fromDateType()),
    };

    this.recurrenceIntervalChanged.emit(newRecurrenceInterval);
  }

  protected handleToNumberChanged($event: number) {
    const newRecurrenceInterval: TimeSpanDto = {
      ...this.recurrenceInterval(),
      to: this.calculateTimeSpan($event, this.toDateType()),
    };

    this.recurrenceIntervalChanged.emit(newRecurrenceInterval);
  }

  private calculateTimeSpan(number: number, timeSpanType: string) {
    return `${number}${timeSpanType}`;
  }

  protected readonly TimeSpanType = TimeSpanType;
}
